import {
  type Camera,
  Color,
  HalfFloatType,
  Matrix4,
  NoBlending,
  RGBAFormat,
  type Scene,
  ShaderMaterial,
  type Texture,
  Vector2,
  type WebGLRenderer,
  WebGLRenderTarget,
} from "three";
import { GTAOPass } from "three/examples/jsm/postprocessing/GTAOPass.js";
import { FullScreenQuad } from "three/examples/jsm/postprocessing/Pass.js";
import {
  type AmbientOcclusionTuning,
  GTAO_DENOISE,
} from "./ambientOcclusionSettings";
import {
  clampGtaoDenoisePasses,
  clampGtaoDenoiseRadius,
  patchGtaoDenoiseShader,
} from "./gtaoDenoising";
import { MIN_PROBE_PIXELS, patchGtaoHorizonShader } from "./gtaoHorizon";

/**
 * Clamps a requested AO resolution scale to [0.5, 1]; a non-finite request
 * means "no reduction". Only interaction frames may render AO below full
 * resolution — idle frames pass 1, so callers gate on activity themselves.
 */
export function clampAmbientOcclusionResolutionScale(
  requestedScale: number,
): number {
  return Number.isFinite(requestedScale)
    ? Math.min(1, Math.max(0.5, requestedScale))
    : 1;
}

export class ProductViewerGTAOPass extends GTAOPass {
  // Present in Three r179, but omitted from its type declarations.
  declare normalRenderTarget: WebGLRenderTarget;
  private declare readonly _fsQuad: FullScreenQuad;
  private declare readonly _restoreVisibility: () => void;
  readonly denoiseRenderTarget: WebGLRenderTarget;
  readonly compositeMaterial: ShaderMaterial;
  private readonly compositeUniforms;
  private readonly compositeQuad: FullScreenQuad;
  private readonly denoiseInputs;
  private readonly savedClearColor = new Color();
  private denoisePassCount: 1 | 2 = GTAO_DENOISE.passes;
  private disposed = false;
  private resolutionScale = 1;
  private outputWidth: number;
  private outputHeight: number;

  constructor(
    scene: Scene,
    camera: Camera,
    private readonly beautyTarget: WebGLRenderTarget,
    width: number,
    height: number,
  ) {
    super(scene, camera, width, height);
    this.outputWidth = width;
    this.outputHeight = height;
    this.gtaoRenderTarget.depthBuffer = false;
    this.pdRenderTarget.depthBuffer = false;
    const index = this.pdMaterial.uniforms.index;
    const tDiffuse = this.pdMaterial.uniforms.tDiffuse;
    if (!index || !tDiffuse) {
      throw new Error("GTAO denoiser is missing its input/index uniforms");
    }
    this.denoiseInputs = { index, tDiffuse };
    // Never probe inside a pixel: upstream's ladder starts at 1.4 mm, which on
    // a floor is the same texel read back as noise, and the horizon only
    // accumulates upward so it biases toward occlusion. See gtaoHorizon.
    this.gtaoMaterial.fragmentShader = patchGtaoHorizonShader(
      this.gtaoMaterial.fragmentShader,
    );
    // The clamp divides by `resolution`, which is the ambient occlusion
    // buffer. Pre-multiplying by the scale keeps the ratio the depth buffer's,
    // which is always full size — see MIN_PROBE_PIXELS. setSize keeps it current.
    this.gtaoMaterial.uniforms.minProbePixels = { value: MIN_PROBE_PIXELS };
    this.gtaoMaterial.needsUpdate = true;
    this.pdMaterial.fragmentShader = patchGtaoDenoiseShader(
      this.pdMaterial.fragmentShader,
    );
    this.pdMaterial.blending = NoBlending;
    this.pdMaterial.needsUpdate = true;
    this.updatePdMaterial({
      radius: GTAO_DENOISE.radius,
      rings: GTAO_DENOISE.rings,
      radiusExponent: GTAO_DENOISE.radiusExponent,
      lumaPhi: GTAO_DENOISE.lumaPhi,
      depthPhi: GTAO_DENOISE.depthPhi,
      normalPhi: GTAO_DENOISE.normalPhi,
      samples: 16,
    });
    // Allocate once, including for one-pass A/B mode. No fullscreen MSAA.
    this.denoiseRenderTarget = new WebGLRenderTarget(width, height, {
      type: HalfFloatType,
      format: RGBAFormat,
      depthBuffer: false,
      stencilBuffer: false,
      samples: 0,
    });
    this.denoiseRenderTarget.texture.name = "ProductViewerGTAODenoise";
    this.compositeUniforms = {
      tBeauty: { value: beautyTarget.texture },
      tAO: { value: this.gtaoMap },
      tDepth: { value: this.depthTexture },
      tNormal: { value: this.normalTexture },
      aoResolution: { value: new Vector2(width, height) },
      projectionInverse: { value: new Matrix4() },
      intensity: { value: 1 },
      upsample: { value: false },
      aoOnly: { value: false },
    };
    this.compositeMaterial = new ShaderMaterial({
      name: "ProductViewerAOComposite",
      uniforms: this.compositeUniforms,
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = vec4(position.xy, 0.0, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        uniform sampler2D tBeauty;
        uniform sampler2D tAO;
        uniform sampler2D tDepth;
        uniform sampler2D tNormal;
        uniform vec2 aoResolution;
        uniform mat4 projectionInverse;
        uniform float intensity;
        uniform bool upsample;
        uniform bool aoOnly;
        varying vec2 vUv;

        vec3 viewPosition(vec2 uv, float depth) {
          vec4 clip = vec4(vec3(uv, depth) * 2.0 - 1.0, 1.0);
          vec4 position = projectionInverse * clip;
          return position.xyz / position.w;
        }

        vec4 ambientOcclusion() {
          if (!upsample) return texture2D(tAO, vUv);
          float depth = texture2D(tDepth, vUv).x;
          if (depth >= 1.0) return vec4(1.0);
          vec3 position = viewPosition(vUv, depth);
          vec3 normal = normalize(
            texture2D(tNormal, vUv).xyz * 2.0 - 1.0
          );
          vec2 pixel = vUv * aoResolution - 0.5;
          vec2 base = floor(pixel);
          vec2 fraction = fract(pixel);
          vec4 result = vec4(0.0);
          float weightSum = 0.0;
          for (int y = 0; y < 2; y++) {
            for (int x = 0; x < 2; x++) {
              vec2 offset = vec2(float(x), float(y));
              vec2 sampleUv = clamp((base + offset + 0.5) / aoResolution,
                0.5 / aoResolution, 1.0 - 0.5 / aoResolution);
              float sampleDepth = texture2D(tDepth, sampleUv).x;
              if (sampleDepth >= 1.0) continue;
              vec3 sampleNormal = normalize(
                texture2D(tNormal, sampleUv).xyz * 2.0 - 1.0
              );
              vec3 samplePosition = viewPosition(sampleUv, sampleDepth);
              float planeDistance = abs(
                dot(samplePosition - position, normal)
              );
              float tolerance = max(0.001, abs(position.z) * 0.002);
              vec2 bilinear = mix(1.0 - fraction, fraction, offset);
              float weight = bilinear.x * bilinear.y
                * pow(max(dot(normal, sampleNormal), 0.0), 8.0)
                * exp(-planeDistance / tolerance);
              result += texture2D(tAO, sampleUv) * weight;
              weightSum += weight;
            }
          }
          return weightSum > 0.0001 ? result / weightSum : vec4(1.0);
        }

        void main() {
          vec4 ao = ambientOcclusion();
          vec4 beauty = texture2D(tBeauty, vUv);
          vec3 visibility = mix(vec3(1.0), ao.rgb, intensity);
          gl_FragColor = aoOnly
            ? ao
            : vec4(beauty.rgb * visibility, beauty.a);
        }
      `,
      blending: NoBlending,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
    });
    this.compositeQuad = new FullScreenQuad(this.compositeMaterial);
  }

  get denoisePasses(): 1 | 2 {
    return this.denoisePassCount;
  }

  /** The final filtered texture; raw debug still reads gtaoRenderTarget. */
  override get gtaoMap(): Texture {
    return this.denoisePassCount === 2
      ? this.denoiseRenderTarget.texture
      : this.pdRenderTarget.texture;
  }

  /** Uniform/target selection only: no recompile, allocation or idle work. */
  setDenoisePasses(count: 1 | 2): void {
    this.denoisePassCount = clampGtaoDenoisePasses(count);
    this.compositeUniforms.tAO.value = this.gtaoMap;
  }

  setResolutionScale(scale: number): void {
    const next = clampAmbientOcclusionResolutionScale(scale);
    if (next === this.resolutionScale) return;
    this.resolutionScale = next;
    this.setSize(this.outputWidth, this.outputHeight);
  }

  setDenoiseSamples(samples: 8 | 16): void {
    if (this.pdSamples !== samples) this.updatePdMaterial({ samples });
  }

  applyTuning(tuning: AmbientOcclusionTuning): void {
    this.updateGtaoMaterial({
      radius: tuning.radius,
      scale: tuning.intensity,
      samples: tuning.samples,
    });
    this.updatePdMaterial({
      radius: clampGtaoDenoiseRadius(tuning.denoiseRadius),
    });
  }

  setBufferOutput(showBuffer: boolean): void {
    this.setDiagnosticView(showBuffer ? "ao" : "composed");
  }

  setDiagnosticView(view: "composed" | "ao"): void {
    this.output =
      view === "ao" ? GTAOPass.OUTPUT.Denoise : GTAOPass.OUTPUT.Default;
  }

  override setSize(width: number, height: number): void {
    this.outputWidth = width;
    this.outputHeight = height;
    const aoWidth = Math.max(1, Math.floor(width * this.resolutionScale));
    const aoHeight = Math.max(1, Math.floor(height * this.resolutionScale));
    this.width = aoWidth;
    this.height = aoHeight;
    this.gtaoRenderTarget.setSize(aoWidth, aoHeight);
    this.pdRenderTarget.setSize(aoWidth, aoHeight);
    this.denoiseRenderTarget.setSize(aoWidth, aoHeight);
    this.gtaoMaterial.uniforms.resolution?.value.set(aoWidth, aoHeight);
    const minProbePixels = this.gtaoMaterial.uniforms.minProbePixels;
    if (minProbePixels) {
      minProbePixels.value = MIN_PROBE_PIXELS * this.resolutionScale;
    }
    this.pdMaterial.uniforms.resolution?.value.set(aoWidth, aoHeight);
    // Upsampling needs full-resolution geometry at the destination pixel.
    this.normalRenderTarget.setSize(width, height);
    this.compositeUniforms.aoResolution.value.set(aoWidth, aoHeight);
    this.compositeUniforms.upsample.value =
      aoWidth !== width || aoHeight !== height;
  }

  override render(
    renderer: WebGLRenderer,
    writeBuffer: WebGLRenderTarget,
    _readBuffer: WebGLRenderTarget,
    deltaTime: number,
    maskActive: boolean,
  ): void {
    const output = this.output;
    const composite =
      output === GTAOPass.OUTPUT.Default || output === GTAOPass.OUTPUT.Denoise;
    const filter = composite || output === GTAOPass.OUTPUT.Off;
    const overrideMaterial = this.scene.overrideMaterial;
    const quadMaterial = this._fsQuad.material;
    const autoClear = renderer.autoClear;
    renderer.getClearColor(this.savedClearColor);
    const clearAlpha = renderer.getClearAlpha();
    const previousTarget = renderer.getRenderTarget();
    const cubeFace = renderer.getActiveCubeFace?.() ?? 0;
    const mipLevel = renderer.getActiveMipmapLevel?.() ?? 0;
    let completed = false;

    try {
      this.denoiseInputs.index.value = 0;
      this.denoiseInputs.tDiffuse.value = this.gtaoRenderTarget.texture;
      if (composite) this.output = GTAOPass.OUTPUT.Off;
      super.render(
        renderer,
        writeBuffer,
        this.beautyTarget,
        deltaTime,
        maskActive,
      );

      // Raw AO, normal, depth and diffuse debug retain upstream output.
      if (filter && this.denoisePassCount === 2) {
        this.renderSecondDenoise(renderer);
      }
      if (composite) {
        this.compositeUniforms.tAO.value = this.gtaoMap;
        this.compositeUniforms.intensity.value = this.blendIntensity;
        this.compositeUniforms.aoOnly.value =
          output === GTAOPass.OUTPUT.Denoise;
        this.compositeUniforms.projectionInverse.value.copy(
          this.camera.projectionMatrixInverse,
        );
        renderer.autoClear = false;
        renderer.setRenderTarget(this.renderToScreen ? null : writeBuffer);
        this.compositeQuad.render(renderer);
      }
      completed = true;
    } finally {
      // Upstream's helpers have no finally, including the G-buffer override.
      this.output = output;
      this.denoiseInputs.index.value = 0;
      this.denoiseInputs.tDiffuse.value = this.gtaoRenderTarget.texture;
      this._fsQuad.material = quadMaterial;
      this.scene.overrideMaterial = overrideMaterial;
      this._restoreVisibility();
      renderer.autoClear = autoClear;
      renderer.setClearColor(this.savedClearColor);
      renderer.setClearAlpha(clearAlpha);
      // Successful output stays bound for the composer; failures roll back.
      if (!completed) {
        renderer.setRenderTarget(previousTarget, cubeFace, mipLevel);
      }
    }
  }

  private renderSecondDenoise(renderer: WebGLRenderer): void {
    const material = this.compositeQuad.material;
    try {
      this.denoiseInputs.tDiffuse.value = this.pdRenderTarget.texture;
      this.denoiseInputs.index.value = 1;
      this.compositeQuad.material = this.pdMaterial;
      renderer.autoClear = false;
      renderer.setRenderTarget(this.denoiseRenderTarget);
      // Rejected centers discard: clear to unoccluded, not last frame's AO.
      renderer.setClearColor(0xffffff);
      renderer.setClearAlpha(1);
      renderer.clear();
      this.compositeQuad.render(renderer);
    } finally {
      this.denoiseInputs.tDiffuse.value = this.gtaoRenderTarget.texture;
      this.denoiseInputs.index.value = 0;
      this.compositeQuad.material = material;
    }
  }

  override dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    const renderTargets = [
      this.normalRenderTarget,
      this.gtaoRenderTarget,
      this.pdRenderTarget,
      this.denoiseRenderTarget,
    ];
    const materials = [
      this.normalMaterial,
      this.gtaoMaterial,
      this.pdMaterial,
      this.copyMaterial,
      this.depthRenderMaterial,
      this.blendMaterial,
      this.compositeMaterial,
    ];
    const noiseTextures = [this.gtaoNoiseTexture, this.pdNoiseTexture];
    // Target attachments, including G-buffer depth, belong to their targets.
    for (const target of renderTargets) target.dispose();
    for (const material of materials) material.dispose();
    for (const texture of noiseTextures) texture.dispose();
    // Neither super.dispose() nor either quad's dispose(): FullScreenQuad
    // frees the module-level triangle shared by all composers. These quads
    // own no geometry; their materials are released explicitly above.
    // beautyTarget is borrowed and remains the beauty pass's responsibility.
  }
}
